import { hideLoading, showLoading } from 'react-redux-loading-bar';
import { all, call, delay, fork, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { handleFrontendCacheLoad } from 'customEvents/actions';
import {
    DEFAULT_ADMON_SOURCE_FILTER,
    DEFAULT_APP_FILTER,
    DEFAULT_FILTER_NAMES,
    DEFAULT_SOURCE_FILTER,
    fraudReportTypes,
    isCompareField,
    isDrilldownEnabled,
    isObject,
    isPivotReport,
    isPostProcessFilterField,
    reportTypes,
    translateQueryToStore,
    validateQuery,
} from 'utils/reports';
import { CUSTOMER_TIERS } from 'utils/consts';
import { tick } from '../../global/utils';
import { queryTypeToAPI } from '../../reports/apiMapping';

import {
    CLOSE_SHELF,
    DELETE_REPORT,
    GENERATE_ANONYMOUS_LINK,
    GENERATE_ANONYMOUS_LIST,
    RUN_ANONYMOUS_PAGE,
    runAnonymousPage,
    showPasswordModal,
    toggleDateSelection,
    UPDATE_ANONYMOUS_ID,
    updateCreator,
    updateError,
    updateReportName,
} from '../../actions/anonymous';
import { updatedFilters, updateFields } from '../../actions/fields';
import {
    CHART_LINES_CHANGED,
    CHART_TIME_BREAKDOWN_CHANGED,
    chartResponse,
    CHECK_ALERTS_DATA,
    checkAlertsData,
    CLEAR_TOASTS,
    clearReportData,
    clearToasts,
    DATE_CHANGED_GLOBAL,
    dateChanged,
    DOWNLOAD_REPORT,
    EXPORT_TABLE,
    FIELD_CLICKED,
    FILTER_CHANGED,
    FILTER_VALUE_CHANGED,
    filterAdd,
    filtersExpanded,
    filterValueAdded,
    getExportAction,
    GRID_READY,
    hideChart,
    hideSpinner,
    hideTable,
    MODE_SELECTED_GENERIC,
    modeSelected,
    queryErrorResponse,
    queryRequestParams,
    queryResponse,
    reportFormChanged,
    reportsFieldClicked,
    RUN_QUERY,
    RUN_QUERY_ON_FIELD_CHECKED,
    runQuery as runQueryAction,
    setActionType,
    setChartError,
    setClearSelectionModeOff,
    setRunQueryFinished,
    setValidationError,
    showChart,
    showReportHeader,
    showTable,
    toggleShelf,
    toggleSlimMode,
    transparencyDataResponse,
    UPDATE_CHART,
    UPDATE_QUERY_URL,
    updateAnonymousId,
    updateAnonymousList,
    updateBookmarkData,
    updateChartData,
    updateChartLines,
    updateChartTimeBreakdown,
    updateReportData,
} from '../../actions/reports';
import { setApiKey, UPDATE_USER_DATA } from '../../actions/user';
import { checkActionForType, initialStateBookmark } from '../../reducers/reports';
import reportConfigs from '../../reducers/reportsConfig';
import { getPassword, getSecretId } from '../../selectors/anonymous';
import { getFields, getFieldsForType, getFilters } from '../../selectors/fields';
import {
    getChartConfig,
    getConfigForType,
    getFrozenFilters,
    getIsClearSelectionOn,
    getPostProcessFiltersUI,
    getQueryDataForType,
    getQueryRunning,
    getReportDataForType,
    getReportDates,
    getShelfShown,
    getShouldDrilldown,
    getTableConfig,
    getTableFormattedData,
} from '../../selectors/reports';
import { getUserData } from '../../selectors/user';
import { Reports } from '../../services';
import GridService from '../../services/grid';
import { getExportFileName } from '../../utils/export';
import { getDefaultCompareDates, getDefaultDates } from '../../utils/regional';

import toaster from '../../utils/toaster';
import {
    deserializeReportQuery,
    extractFromUrl,
    getImpersonationFromQuery,
    ReportUrlParams,
    searchParamsWithReportQuery,
    serializeReportQuery,
    serializeReportQueryEx,
    setUrlSearchParams,
} from '../../utils/url';
import Cancelable from '../Cancelable';
import {
    getFieldsDict,
    updateCreativesAIDimensions,
    updateFieldsHandler,
    updateFilterFieldsHandler,
    updateReportFields,
    updateSourcesFieldsAvailability,
} from '../fields';

import { calculatedMetricValue, isFacebookCensored, shouldRowBeSkipped } from '../../utils/grids';
import { buildCSVFromReportData, downloadCSV } from '../../utils/csvExport';
import { REPORT_QUERY_PREFIX, trackMixpanelEvent } from '../../utils/general';
import {
    AI_DIMENSION_TYPES,
    COMPARE_MODE,
    HAS_CREATIVE_PREVIEW_FILTER,
    HAS_CREATIVE_PREVIEW_VALUE,
} from '../../components/pages/reports/consts';
import { REPORT_EXCEEDS_SIZE_LIMIT_ERROR, SOURCE_FIELD } from '../../utils/reportsConsts';
import { getTranslate } from '../../selectors/locale';

const cancelable = new Cancelable();

const reportsAPI = new Reports();
const gridService = new GridService();

const REPORTS_WITH_DYNAMIC_FIELDS = [reportTypes.creativeExplore];

function* clearReport(reportType, shelfOpen = false) {
    yield all([
        put(setActionType(hideChart(), reportType)),
        put(setActionType(hideTable(), reportType)),
        put(setActionType(toggleShelf(shelfOpen), reportType)),
        put(setActionType(setValidationError(''), reportType)),
        put(clearToasts()),
        put(setActionType(updateAnonymousId(null), reportType)),
        put(setActionType(clearReportData(), reportType)),
        put(setActionType(reportFormChanged(true), reportType)),
    ]);
}

function* onload(action) {
    const start = new Date().getTime();
    const reportConfig = reportConfigs.find(config => config.path === action.payload.pathname);
    const reportType = reportConfig.type;
    const isRunning = yield select(getQueryRunning(reportType));
    const isUrlQuery = searchParamsWithReportQuery(action.payload.search);

    if (!isRunning) {
        if (isUrlQuery) {
            yield all([call(updateFilterFieldsHandler, reportType), call(updateCreativesAIDimensions, reportType)]);
        } else {
            yield fork(updateFilterFieldsHandler, reportType);
            yield fork(updateCreativesAIDimensions, reportType);
        }

        // Update the fields. if there's no search data (i.e. pristine) - base on defaults. Otherwise - base on the query
        yield call(updateFieldsHandler, reportType, !isUrlQuery);

        yield fork(updateSourcesFieldsAvailability, reportType);
    }

    if (isUrlQuery) {
        // when the run button is hit, we run the query and change the url.
        // the following line makes sure we're not running it again
        try {
            const userData = yield select(getUserData);
            let forceValues = {};

            if (
                !userData.show_report_toggle ||
                (action.payload.search && action.payload.search.includes('bookmark_id'))
            ) {
                forceValues = { is_slim_mode: false };
            }

            const reportQuery = deserializeReportQuery(action.payload.search, forceValues);
            const fields = yield select(getFields);
            const store = translateQueryToStore(reportQuery, fields);

            if (!store.dates.start_date || !store.dates.end_date) {
                store.dates = {
                    ...store.dates,
                    ...getDefaultDates(),
                };
            }
            const isStandardAnalytics = window.organization_tier === CUSTOMER_TIERS.STANDARD_ANALYTICS;
            const defaultFieldsToAdd = [];

            if (!isStandardAnalytics) {
                defaultFieldsToAdd.push(
                    store.filters[store.filters.findIndex(element => element.field === DEFAULT_APP_FILTER.field)] ||
                        DEFAULT_APP_FILTER
                );
            }

            if (reportType !== 'adMonetization') {
                defaultFieldsToAdd.push(
                    store.filters[store.filters.findIndex(element => element.field === DEFAULT_SOURCE_FILTER.field)] ||
                        DEFAULT_SOURCE_FILTER
                );
            } else {
                defaultFieldsToAdd.push(
                    store.filters[
                        store.filters.findIndex(element => element.field === DEFAULT_ADMON_SOURCE_FILTER.field)
                    ] || DEFAULT_ADMON_SOURCE_FILTER
                );
            }

            store.filters = [
                ...defaultFieldsToAdd,
                ...store.filters.filter(element => !DEFAULT_FILTER_NAMES.includes(element.field)),
            ];

            if (!store.dates.start_date_2 || !store.dates.end_date_2 || store.mode !== COMPARE_MODE) {
                store.dates = {
                    ...store.dates,
                    ...getDefaultCompareDates(store.dates.start_date, store.dates.end_date),
                };
            }

            yield put(setActionType(updateReportData(store), reportType));

            if (store.api_key) {
                yield put(setApiKey(store.api_key));
            }

            // runQueryAction needs to run after the updateReportData - as it selects the needed data from the store
            yield put(setActionType(runQueryAction(reportType, reportType, null, store.bookmark, true), reportType));
        } catch (e) {
            yield call(updateFieldsHandler, reportType, true);
            yield call(clearReport, reportType, true);
            yield put(setActionType(hideSpinner(), reportType));
            yield put(setActionType(showReportHeader(), reportType));
            toaster.error('The report link is broken.');
        }
    } else {
        yield call(clearReport, reportType, true);
        yield put(setActionType(hideSpinner(), reportType));
        yield put(setActionType(showReportHeader(), reportType));
    }

    yield call(tick);
    yield put(updateFields([false, [], true]));
    const end = new Date().getTime();
    yield put(handleFrontendCacheLoad((end - start) / 1000));
}

function* destroy(currentPage) {
    const reportConfig = reportConfigs.find(config => config.path === currentPage.pathname);
    const reportType = reportConfig.type;
    yield call(clearReport, reportType, true);
    yield put(setActionType(queryResponse(null), reportType));
    yield put(setActionType(filtersExpanded(true), reportType));
    yield put(updatedFilters([]));
    yield put(setActionType(updateBookmarkData(initialStateBookmark), reportType));
}

//  on mode change, changes the default selected fields.
function* updateSelectedFields(action) {
    const { id, reportType } = action;
    yield put(setActionType(modeSelected(id), reportType));
    const fieldKeys = ['metrics', 'cohortMetrics', 'discrepancyMetrics'];
    const fieldsValues = {};
    const isClearSelectionOn = yield select(getIsClearSelectionOn(reportType));

    if (!isClearSelectionOn) {
        for (const fieldKey of fieldKeys) {
            fieldsValues[fieldKey] = yield select(getFieldsForType(fieldKey));
        }
        yield call(updateReportFields, fieldsValues, reportType);
    }
}

function* handleUserDataUpdated(action) {
    const isSlimMode = action.data && action.data.show_report_toggle;

    yield put(setActionType(toggleSlimMode(isSlimMode), reportTypes.mainReport));
    yield put(setActionType(toggleSlimMode(isSlimMode), reportTypes.pivot));
}

function* fetchChartData(action) {
    const { reportType } = action;

    if (reportType.includes(reportTypes.anonymous)) {
        yield call(handleAnonymousChartQuery, reportType);
        return;
    }

    yield put(setActionType(setChartError(false), reportType));

    // Query for charts data
    const selectedChartData = yield select(getQueryDataForType(reportType, 'charts'));

    const dailyBreakdownNoDim =
        selectedChartData.filters.length === 0 &&
        selectedChartData.dimensions.length === 0 &&
        selectedChartData.valid_key_list_for_charts.length > 0;

    if (selectedChartData.filters.length || dailyBreakdownNoDim || selectedChartData.is_goals) {
        yield put(setActionType(queryRequestParams('chart', selectedChartData), reportType));
        try {
            const chartQueryResponse = yield call(queryTypeToAPI[reportType].runQuery, selectedChartData);
            yield put(setActionType(chartResponse(chartQueryResponse), reportType));
        } catch (e) {
            yield put(setActionType(hideChart(), reportType));
        }
    } else {
        console.error('Chart request has no filters', selectedChartData);
        yield put(setActionType(chartResponse(null), reportType));
        yield put(setActionType(setChartError(true), reportType));
    }
}

function updateQueryURL(
    selectedReportsData,
    postProcessFiltersUI,
    pivotTable,
    columnOrder,
    pinnedColumns,
    columnWidths,
    persistColumnWidths
) {
    const reportQueryUrl = serializeReportQuery(
        selectedReportsData,
        postProcessFiltersUI,
        pivotTable,
        columnOrder,
        pinnedColumns,
        columnWidths,
        persistColumnWidths
    );
    const reportQueryUrlDecoded = serializeReportQueryEx(selectedReportsData, postProcessFiltersUI);
    const event = new CustomEvent('routelessRedirect', {
        detail: {
            url: setUrlSearchParams('', window.location.search, reportQueryUrl),
            urlDecoded: setUrlSearchParams(window.location.href, window.location.search, reportQueryUrlDecoded),
        },
    });
    window.dispatchEvent(event);
}

export function* updateQueryURLFromAction(action) {
    const { reportType, pivotState, columnOrder, pinnedColumns, columnWidths, persistColumnWidths } = action;

    const selectedReportsData = yield select(getQueryDataForType(reportType));

    const filters = yield select(getFilters);
    selectedReportsData.filters = selectedReportsData.filters.map(filter => {
        const filterData = filters.find(({ name }) => name === filter.dimension);
        return {
            ...filter,
            selected_filter_values:
                filter.values?.map(tag =>
                    filterData && isPostProcessFilterField(filterData)
                        ? { name: tag, display_name: tag }
                        : filterData?.values?.find(value => value.name === tag)
                ) || [],
        };
    });
    const postProcessFiltersUI = yield select(getPostProcessFiltersUI(reportType));

    updateQueryURL(
        selectedReportsData,
        postProcessFiltersUI,
        pivotState,
        columnOrder,
        pinnedColumns,
        columnWidths,
        persistColumnWidths
    );
}

function* shouldDrilldown(reportType, requestParams) {
    const tableConfig = yield select(getTableConfig(reportType));
    const state = yield select();

    return isDrilldownEnabled(reportType, tableConfig?.drilldownConfig, state, { requestParams });
}

function* generateAndValidateQuery(
    reportType,
    queryType,
    isAsync = false,
    isDownload = false,
    isExportGsheet = false,
    isCopyCsv = false,
    gsheetToken = null
) {
    const selectedReportsData = yield select(getQueryDataForType(reportType, queryType));
    const userData = yield select(getUserData);
    selectedReportsData.is_async = isAsync;
    selectedReportsData.is_download_report = isDownload;
    selectedReportsData.should_drilldown = yield shouldDrilldown(reportType, selectedReportsData);
    selectedReportsData.is_export_gsheet = isExportGsheet;
    selectedReportsData.is_copy_csv = isCopyCsv;
    selectedReportsData.gsheet_token = gsheetToken;

    const filterFields = yield select(getFilters);
    const { result, message, data } = validateQuery(
        selectedReportsData,
        userData.report_date_limit?.restrictions,
        reportType,
        filterFields
    );

    if (!result) {
        yield put(setActionType(setValidationError(message, data), reportType));
        return null;
    }

    return selectedReportsData;
}
const _getDurationInSeconds = startTimer => {
    return (new Date().getTime() - startTimer) / 1000.0;
};

const _getDurationBucket = durationInSeconds => {
    if (typeof durationInSeconds !== 'number' || isNaN(durationInSeconds)) return 'Undefined';
    if (durationInSeconds < 0) return 'Unknown';
    if (durationInSeconds < 2) return '0s-2s';
    if (durationInSeconds < 4) return '2s-4s';
    if (durationInSeconds < 7) return '4s-7s';
    if (durationInSeconds < 10) return '7s-10s';
    if (durationInSeconds < 25) return '10s-25s';
    if (durationInSeconds < 40) return '25s-40s';
    if (durationInSeconds < 60) return '40s-60s';
    if (durationInSeconds < 120) return '1m-2m';
    if (durationInSeconds < 240) return '2m-4m';
    if (durationInSeconds < 360) return '4m-6m';
    if (durationInSeconds < 480) return '6m-8m';
    if (durationInSeconds < 600) return '8m-10m';
    if (durationInSeconds < 900) return '10m-15m';
    if (durationInSeconds < 1200) return '15m-20m';
    if (durationInSeconds < 1800) return '20m-30m';
    return '>30m';
};

const _getNumRowsBucket = numRows => {
    if (typeof numRows !== 'number' || isNaN(numRows)) return 'Undefined';
    if (numRows < 0) return 'Unknown';
    if (numRows < 1) return '0';
    if (numRows < 100) return '1-100';
    if (numRows < 1000) return '100-1k';
    if (numRows < 5000) return '1k-5K';
    if (numRows < 10000) return '5K-10K';
    if (numRows < 30000) return '10K-30K';
    if (numRows < 50000) return '30K-50K';
    if (numRows < 75000) return '50K-75K';
    if (numRows < 100000) return '75K-100K';
    if (numRows < 300000) return '100K-300K';
    if (numRows < 500000) return '300K-500K';
    if (numRows < 750000) return '500K-750K';
    if (numRows < 1000000) return '750K-1M';
    if (numRows < 3000000) return '1M-3M';
    if (numRows < 5000000) return '3M-5M';
    if (numRows < 10000000) return '5M-10M';
    return '>10m';
};

function* fetchTableData(reportType, queryType) {
    const startTimer = new Date().getTime();
    // Query for reports data
    yield put(setActionType(checkAlertsData(reportType), reportType));

    const selectedReportsData = yield generateAndValidateQuery(reportType, queryType);
    if (!selectedReportsData) {
        return null;
    }

    // store a snapshot of the params at query time (as the selectors are bound to the shelf, and will update when it changes).
    yield put(setActionType(queryRequestParams('table', selectedReportsData), reportType));

    const postProcessFiltersUI = yield select(getPostProcessFiltersUI(reportType));
    const tableConfig = yield select(getTableConfig(reportType));

    const pivotTable = tableConfig.pivot_table ?? extractFromUrl(ReportUrlParams.PIVOT_TABLE);
    const columnOrder = tableConfig.column_order ?? extractFromUrl(ReportUrlParams.COLUMN_ORDER);
    const pinnedColumns = tableConfig.pin_columns ?? extractFromUrl(ReportUrlParams.PINNED_COLUMNS);
    const columnWidths = tableConfig.column_widths ?? extractFromUrl(ReportUrlParams.COLUMN_WIDTHS);
    const persistColumnWidths =
        tableConfig.persist_column_widths ?? extractFromUrl(ReportUrlParams.PERSIST_COLUMN_WIDTHS);

    updateQueryURL(
        selectedReportsData,
        postProcessFiltersUI,
        pivotTable,
        columnOrder,
        pinnedColumns,
        columnWidths,
        persistColumnWidths
    );

    try {
        const response = yield call(queryTypeToAPI[queryType].runQuery, selectedReportsData);
        if (typeof response === 'string' && response.includes(REPORT_EXCEEDS_SIZE_LIMIT_ERROR)) {
            const translate = yield select(getTranslate);
            toaster.error(translate('STATIC.PAGES.REPORTS.REPORT_EXCEEDS_SIZE_LIMIT_ERROR'));
            const durationInSeconds = _getDurationInSeconds(startTimer);
            const durationBucket = _getDurationBucket(durationInSeconds);
            yield put(setActionType(queryErrorResponse(response, durationBucket), reportType));
            return null;
        }

        response.durationInSeconds = _getDurationInSeconds(startTimer);
        response.durationBucket = _getDurationBucket(response.durationInSeconds);
        response.numRowsBucket = _getNumRowsBucket(response.num_of_rows);

        yield put(setActionType(queryResponse(response, reportType), reportType));
        yield put(setActionType(showTable(), reportType));
        return selectedReportsData;
    } catch (e) {
        console.error(e);
        toaster.error('An unexpected error has occurred, please try again later');
        const durationInSeconds = _getDurationInSeconds(startTimer);
        const durationBucket = _getDurationBucket(durationInSeconds);
        yield put(setActionType(queryErrorResponse(e.message, durationBucket), reportType));
        return null;
    }
}

function* _transparencyRequest(reportType) {
    const { reportId } = yield select(getTableFormattedData(reportType));
    const startTime = new Date().getTime();
    let response = {};
    let result = false;
    try {
        response = yield call(reportsAPI.getTransparencyData, reportId);
        result = true;
    } catch (e) {
        console.error(e);
    }
    return { response, duration: new Date().getTime() - startTime, result };
}

function* _transparencyAct(response, reportType, duration, result) {
    yield put(setActionType(transparencyDataResponse(response, duration, result), reportType));
}

// the private version of handleTransparencyData has delay in it. Should we use the public version, the transparency and
// the chart will fire, and resolve, short thereafter, ensuing with a notable time of irresponsiveness.
// WARNING do not use this version (use the public one) as this one's delayed.
export function* _handleTransparencyData(reportType, span = 350) {
    const { response, duration, result } = yield _transparencyRequest(reportType);
    yield delay(span);
    yield _transparencyAct(response, reportType, duration, result);
}

function* runQueryOnFieldClicked(action) {
    const { field, isSingle, reportType, queryType, bookmark } = action;

    yield put(setActionType(reportsFieldClicked(field, isSingle), reportType));
    yield call(runQuery, { reportType, queryType, bookmark });
}

function* downloadOrExportReport(action) {
    const { reportType, queryType, dontExport, origin, isExportGsheet, gsheetToken, isCopyCsv } = action;

    const shelfShown = yield select(getShelfShown(reportType));
    const table = yield select(getTableConfig(reportType));

    if (!shelfShown && !dontExport && table.request) {
        yield fork(handleExportTable, action);
        return;
    }

    const selectedReportsData = yield generateAndValidateQuery(
        reportType,
        queryType,
        true,
        true,
        isExportGsheet,
        isCopyCsv,
        gsheetToken
    );

    if (!selectedReportsData) {
        return;
    }

    const downloadWindow = window.open('about:blank', '_blank');

    if (!downloadWindow) {
        window.alert('Please enable popup windows and retry');
        return;
    }

    try {
        const response = yield call(reportsAPI.runAsyncReport, selectedReportsData);
        if (!response.report_id) {
            return;
        }
        const exportAction = getExportAction(isExportGsheet, isCopyCsv);

        trackMixpanelEvent(REPORT_QUERY_PREFIX, 'Export', {
            report_id: response.report_id,
            async_download: true,
            report_type: reportType,
            action: exportAction,
            origin,
        });
        let url = `/logs/download?report_id=${response.report_id}`;
        const impersonatedUser = getImpersonationFromQuery();
        if (impersonatedUser) {
            url += `&user=${decodeURIComponent(impersonatedUser)}`;
        }
        downloadWindow.location.href = url;
        downloadWindow.focus();
    } catch (e) {
        console.error(e);
    }
}

function* isChartEnabled(reportType, isCompareMode) {
    const chartConfig = yield select(getChartConfig(reportType));
    return !!chartConfig?.enabled && !isCompareMode;
}

export const runQuery = cancelable.wrap(function* runQueryInner(action) {
    const { reportType, queryType, bookmark } = action;

    yield call(clearReport, reportType);

    if (bookmark && bookmark.bookmark_id) {
        yield put(setActionType(updateBookmarkData(bookmark), reportType));
    }

    const tableRequest = yield call(fetchTableData, reportType, queryType);

    if (!tableRequest) {
        yield put(setActionType(toggleShelf(true), reportType));
        yield put(setActionType(showReportHeader(), reportType));
    } else {
        yield take(GRID_READY);

        const shouldCallChart = yield isChartEnabled(reportType, !!tableRequest.compare);

        if (shouldCallChart) {
            yield put(setActionType(hideChart(), reportType));
        }

        const shouldShowDrilldown = yield select(getShouldDrilldown(reportType));

        if (shouldShowDrilldown) {
            yield fork(_handleTransparencyData, reportType);
        }
    }

    yield put(setActionType(setRunQueryFinished(), reportType));
});

function* handleAnonymousChartQuery(reportType) {
    const password = yield select(getPassword);
    const secretId = yield select(getSecretId);
    const selectedChartData = yield select(getQueryDataForType(reportType, 'charts'));
    selectedChartData.chart = true;
    yield put(setActionType(queryRequestParams('chart', selectedChartData), reportType));

    try {
        const responseChart = yield call(reportsAPI.runAnonymousQuery, secretId, password, selectedChartData);
        responseChart.total = undefined;
        yield put(setActionType(chartResponse(responseChart), reportType));
    } catch (e) {
        console.error(e);
    }
}

function* handleChartLinesChanged(action) {
    const { reportType, value } = action;
    yield put(setActionType(updateChartLines(value), reportType));
    if (reportType.includes(reportTypes.anonymous)) {
        yield call(handleAnonymousChartQuery, reportType);
    } else {
        yield put(updateChartData(reportType));
    }
}

function* handleChartTimeBreakdownChanged(action) {
    const { reportType, value } = action;
    yield put(setActionType(updateChartTimeBreakdown(value), reportType));
    if (reportType.includes(reportTypes.anonymous)) {
        yield call(handleAnonymousChartQuery, reportType);
    } else {
        yield put(updateChartData(reportType));
    }
}

function* handleTableFilterChange(action) {
    const { reportType } = action;
    if (reportType.includes(reportTypes.anonymous)) {
        yield fork(handleAnonymousChartQuery, reportType);
    } else {
        const shouldCallChart = yield isChartEnabled(reportType);

        if (shouldCallChart) {
            const chartData = yield select(getConfigForType(reportType).selectors.chartData);
            const chartConfig = yield select(getChartConfig(reportType));
            if (chartData || chartConfig?.show) {
                yield put(updateChartData(reportType));
            }
        }
    }
}

function* handleDatesChangedGlobal(action) {
    const { data, reportType } = action;
    yield put(setActionType(dateChanged(data), reportType));
    if (reportType.includes(reportTypes.anonymous)) {
        yield put(runAnonymousPage());
    } else {
        const { reportData } = yield select(getReportDataForType(reportType));
        if (reportData && Object.keys(reportData).length) {
            yield put(runQueryAction(reportType, reportType));
        }
    }
}

function* handleGenerateAnonymousList() {
    const response = yield call(reportsAPI.generateAnonymousReportsList, {});
    yield put(setActionType(updateAnonymousList(response)));
}

function* handleGenerateAnonymousLink(action) {
    const { name, password, expirationDays, flexibleDates, reportType } = action;
    const tableConfig = yield select(getTableConfig(reportType));

    const query = {
        ...tableConfig.request,
        pivot_table: extractFromUrl(ReportUrlParams.PIVOT_TABLE),
        column_order: extractFromUrl(ReportUrlParams.COLUMN_ORDER),
        pin_columns: extractFromUrl(ReportUrlParams.PINNED_COLUMNS),
        column_widths: extractFromUrl(ReportUrlParams.COLUMN_WIDTHS),
        persist_column_widths: extractFromUrl(ReportUrlParams.PERSIST_COLUMN_WIDTHS),
    };

    // Add a fraud type for filtering fraud data
    if (fraudReportTypes.includes(reportType)) {
        query.fraud_type = reportType.replace('fraud', '').toLowerCase();
    }

    const filters = yield select(getFrozenFilters(reportType));
    const response = yield call(reportsAPI.generateSharedReportLink, {
        report_name: name,
        report_password: password,
        expiration_days: expirationDays,
        query: {
            query,
            available_filters: filters,
        },
        flexible_dates: flexibleDates,
        url: window.location.href,
        secret_id: null,
        shared_reports_list: null,
    });
    yield put(setActionType(updateAnonymousId(response.value.secret_id), reportType));
    yield put(setActionType(updateAnonymousList(response.value.shared_reports_list)));
}

function* handleShelfClose(action) {
    const { reportType } = action;

    yield put(setActionType(updateAnonymousId(''), reportType));
}

function* handleDeleteReport(action) {
    const { secretId } = action;
    const response = yield call(reportsAPI.deleteSharedReport, {
        secretId,
        deleted: false,
        shared_reports_list: null,
    });
    yield put(setActionType(updateAnonymousList(response.value.shared_reports_list)));
}

function* handleClearToasts() {
    toaster.dismiss();
    yield delay(0);
}

function* handleCheckAlertsData(action) {
    const { reportType } = action;
    const userData = yield select(getUserData);

    // continue only for non xorg users and non demo emails
    if (userData.xorg_count !== 0 || !userData.email.includes('demo@singular.net')) {
        return Promise.resolve();
    }

    const selectedReportsData = yield select(getQueryDataForType(reportType));
    const {
        start_date,
        end_date,
        metrics = [],
        discrepancy_metrics = [],
        cohort_metrics = [],
        is_fraud,
        is_admon_report,
    } = selectedReportsData;
    const metricsCopy = [...new Set([...metrics, 'adn_cost'])];
    const columns = { metrics, discrepancy_metrics, cohort_metrics };
    const filters = yield select(getConfigForType(reportType).selectors.formattedFilters);

    try {
        const resp = yield call(reportsAPI.getAlertsData, {
            api_params: {
                start_date,
                end_date,
                metrics: metricsCopy,
                columns,
                filters,
                is_fraud,
                is_admon_report,
            },
        });
        const reportAlerts = resp.alerts;
        const { restrictions } = resp;
        const columnRestrictions = restrictions.column || [];

        const alertsCount = reportAlerts.length;
        const columnRestrictionsCount = columnRestrictions.length;
        const maxSources = 3;

        put(clearToasts());
        if (alertsCount > 0) {
            let alertMsg = '';

            if (alertsCount <= maxSources) {
                reportAlerts.forEach((src, ind) => {
                    alertMsg += src + (reportAlerts.length === ind + 1 ? '' : ', ');
                });
            } else {
                alertMsg = `${reportAlerts[0]}, ${reportAlerts[1]}, ${reportAlerts[2]} and ${(
                    alertsCount - 3
                ).toString()} other`;
            }

            toaster.error(
                `We are experiencing issues with logging into ${alertMsg}. Please visit the Data Connectors page to validate and update the credentials.`
            );
        }

        if (columnRestrictionsCount > 0) {
            const columnRestrictionsMessage = `'${columnRestrictions.join("', '")}'`;
            toaster.warning(
                `The following columns are restricted for this user, Please remove them from the query: ${columnRestrictionsMessage}`
            );
        }
    } catch (e) {
        console.error(e);
    }
}

function* handleExportTable(action) {
    const { reportType, isExportGsheet, gsheetToken } = action;
    const table = yield select(getTableConfig(reportType));
    const { request } = table;

    const exportParams = {
        fileName: getExportFileName(
            request.time_breakdown[0],
            {
                start_date: request.start_date,
                end_date: request.end_date,
                start_date_2: request.start_date_2,
                end_date_2: request.end_date_2,
            },
            request.compare
        ),
        allColumns: true,
        skipPinnedTop: true,
        shouldRowBeSkipped,
        processCellCallback: props => {
            const { value, column } = props;
            if (isCompareField(column.colId)) return props.column.colDef.valueFormatter(props);
            return isObject(value) ? calculatedMetricValue(value) : value;
        },
    };

    const reportConfig = getConfigForType(reportType);

    if (reportConfig?.getExportHeader) {
        exportParams.processHeaderCallback = ({ column }) => {
            const { colDef } = column;
            return reportConfig.getExportHeader(colDef);
        };
    }

    const api = gridService.gridApi(`report_${reportType}`);

    const isPivot = isPivotReport(reportType);
    let { reportData } = yield select(getReportDataForType(reportType));
    const { fileName } = exportParams;

    if (isPivot && reportData?.results) {
        reportData = {
            ...reportData,
            results: reportData.results.filter(row => !isFacebookCensored(row)),
        };
    }

    if (isExportGsheet) {
        const csvData = isPivot ? buildCSVFromReportData(reportData) : api.getDataAsCsv(exportParams);
        const resp = yield call(reportsAPI.exportDataToGsheet, fileName, gsheetToken, csvData);
        if (resp.error_message === 'report_too_big') {
            const translate = yield select(getTranslate);
            toaster.error(translate('STATIC.PAGES.REPORTS.GSHEET_REPORT_TOO_BIG'));
            return;
        }
        if (resp) {
            const gsheetUrl = resp.gsheet_url;
            const downloadWindow = window.open('about:blank', '_blank');
            downloadWindow.location.href = gsheetUrl;
            downloadWindow.focus();
        }
    } else if (isPivot) {
        const csvData = buildCSVFromReportData(reportData);
        downloadCSV(csvData, fileName);
    } else {
        api.exportDataAsCsv(exportParams);
    }

    yield delay(0);
}

function* handleAnonymousError(errorMessage) {
    yield put(updateError(errorMessage));
}

function* handleAnonymousSuccess(secretId, password, reportType, response) {
    const { query: responseQuery, available_filters: availableFilters } = response.query;
    if (responseQuery.compare) {
        yield put(modeSelected(COMPARE_MODE));
    }
    if (response.flexible_dates) {
        yield put(toggleDateSelection(true));
    }
    if (response.report_creator) {
        yield put(updateCreator(response.report_creator));
    }
    if (response.reportName) {
        yield put(updateReportName(response.reportName));
    }
    yield put(setActionType(queryRequestParams('table', responseQuery), reportType));
    yield put(
        setActionType(
            updateReportData({
                selectedFields: {
                    // !!IMPORTANT (Anonymous Report) - here are only sections relevant and common for all Singular report types!!
                    dimensions: responseQuery.dimensions,
                    cohortMetrics: responseQuery.cohort_metrics,
                    cohortPeriods: responseQuery.cohort_periods,
                    timeBreakdowns: responseQuery.time_breakdown,
                    sourceAttributionType: responseQuery.source_attribution_type,
                    metrics: responseQuery.metrics,
                },
                filters: [
                    ...responseQuery.filters.map(f => ({
                        operator: f.operator,
                        tags: f.values,
                        field: f.dimension,
                    })),
                    ...(responseQuery.post_process_filters || []).map(f => ({
                        operator: f.operator,
                        tags: [f.value],
                        field: f.field,
                        isPostProcess: true,
                    })),
                ],
                dates: {
                    start_date: responseQuery.start_date,
                    end_date: responseQuery.end_date,
                    start_date_2: responseQuery.start_date_2,
                    end_date_2: responseQuery.end_date_2,
                },
                table: {
                    pivot_table: responseQuery[ReportUrlParams.PIVOT_TABLE],
                    column_order: responseQuery[ReportUrlParams.COLUMN_ORDER],
                    pin_columns: responseQuery[ReportUrlParams.PINNED_COLUMNS],
                    column_widths: responseQuery[ReportUrlParams.COLUMN_WIDTHS],
                    persist_column_widths: responseQuery[ReportUrlParams.PERSIST_COLUMN_WIDTHS],
                },
            }),
            reportType
        )
    );
    yield put(setActionType(queryResponse(response, reportType), reportType));
    yield put(
        updatedFilters({
            dimensions: availableFilters,
        })
    );
    yield put(setActionType(showTable(), reportType));
}

function* handleAnonymousRun() {
    const reportType = 'anonymousReport';
    const secretId = yield select(getSecretId);
    const password = yield select(getPassword);
    yield all([
        put(setActionType(hideChart(), reportType)),
        put(setActionType(hideTable(), reportType)),
        put(showLoading()),
    ]);
    if (secretId) {
        let query = '';
        let errorMessage = 'STATIC.PAGES.ANONYMOUS.ERROR_INVALID';
        const tableConfig = yield select(getTableConfig(reportType));
        if (tableConfig.request) {
            query = tableConfig.request;
            const dates = yield select(getReportDates(reportType));
            Object.assign(query, dates);
        }
        let response;
        try {
            response = yield call(reportsAPI.runAnonymousQuery, secretId, password, query);
        } catch (err) {
            yield put(setActionType(queryResponse(response, reportType), reportType));
            if (err.message === Reports.ERROR_ANONYMOUS_INVALID_EMAIL.message) {
                errorMessage = 'STATIC.PAGES.ANONYMOUS.ERROR_INVALID_EMAIL';
            }
            if (err.message === Reports.ERROR_ANONYMOUS_EXPIRED_ID.message) {
                errorMessage = 'STATIC.PAGES.ANONYMOUS.ERROR_EXPIRED';
            }
            if (err.message === Reports.ERROR_ANONYMOUS_INVALID_ID.message) {
                errorMessage = 'STATIC.PAGES.ANONYMOUS.ERROR_INVALID';
            }
        }
        if (!response) {
            yield call(handleAnonymousError, errorMessage);
        } else {
            yield call(handleAnonymousSuccess, secretId, password, reportType, response);
        }
        yield put(hideLoading());
    }
}

function* handleAnonymousSecretIdUpdate(action) {
    const { secretId } = action;
    const hasPassword = yield call(reportsAPI.hasPassword, secretId);
    if (hasPassword) {
        yield put(showPasswordModal());
    } else {
        yield put(runAnonymousPage());
    }
}

/******************************************************************************/
/** ***************************** WATCHERS *************************************/

/******************************************************************************/

function* watchReportsModeSelected() {
    while (true) {
        const action = yield take(MODE_SELECTED_GENERIC);
        yield fork(updateSelectedFields, action);
    }
}

function _runQueryWatcher(reportType) {
    function* watchRunQuery() {
        yield takeLatest(checkActionForType(RUN_QUERY, reportType), runQuery);
        yield takeEvery(checkActionForType(UPDATE_QUERY_URL, reportType), updateQueryURLFromAction);
    }
    return watchRunQuery;
}

function* watchChartLinesChanged() {
    while (true) {
        const action = yield take(CHART_LINES_CHANGED);
        yield fork(handleChartLinesChanged, action);
    }
}

function* watchChartTimeBreakdownChanged() {
    while (true) {
        const action = yield take(CHART_TIME_BREAKDOWN_CHANGED);
        yield fork(handleChartTimeBreakdownChanged, action);
    }
}

function* watchTableFilterChange() {
    yield takeLatest(FILTER_CHANGED, handleTableFilterChange);
}

function* watchDatesChangedGlobal() {
    while (true) {
        const action = yield take(DATE_CHANGED_GLOBAL);
        yield fork(handleDatesChangedGlobal, action);
    }
}

function* watchGenerateAnonymousList() {
    while (true) {
        const action = yield take(GENERATE_ANONYMOUS_LIST);
        yield fork(handleGenerateAnonymousList, action);
    }
}

function* watchGenerateAnonymousLink() {
    while (true) {
        const action = yield take(GENERATE_ANONYMOUS_LINK);
        yield fork(handleGenerateAnonymousLink, action);
    }
}

function* watchShelfClose() {
    while (true) {
        const action = yield take(CLOSE_SHELF);
        yield fork(handleShelfClose, action);
    }
}

function* watchDeleteReport() {
    while (true) {
        const action = yield take(DELETE_REPORT);
        yield fork(handleDeleteReport, action);
    }
}

function* watchExportTable() {
    while (true) {
        const action = yield take(EXPORT_TABLE);
        yield fork(handleExportTable, action);
    }
}

function* watchAnonymousSecretIdUpdate() {
    while (true) {
        const action = yield take(UPDATE_ANONYMOUS_ID);
        yield fork(handleAnonymousSecretIdUpdate, action);
    }
}

function* watchAnonymousPageRun() {
    while (true) {
        yield take(RUN_ANONYMOUS_PAGE);
        yield fork(handleAnonymousRun);
    }
}

function* watchDownloadReport() {
    yield takeLatest(DOWNLOAD_REPORT, downloadOrExportReport);
}

function* watchGetAlertsData() {
    yield takeLatest(checkActionForType(CHECK_ALERTS_DATA, reportTypes.mainReport), handleCheckAlertsData);
    yield takeLatest(checkActionForType(CHECK_ALERTS_DATA, reportTypes.pivot), handleCheckAlertsData);
    yield takeLatest(checkActionForType(CHECK_ALERTS_DATA, reportTypes.fraudProtected), handleCheckAlertsData);
    yield takeLatest(checkActionForType(CHECK_ALERTS_DATA, reportTypes.fraudSuspicious), handleCheckAlertsData);
    yield takeLatest(checkActionForType(CHECK_ALERTS_DATA, reportTypes.fraudRejected), handleCheckAlertsData);
    yield takeLatest(checkActionForType(CHECK_ALERTS_DATA, reportTypes.adMonetization), handleCheckAlertsData);
    yield takeLatest(checkActionForType(CHECK_ALERTS_DATA, reportTypes.crossDevice), handleCheckAlertsData);
}

function* watchClearToasts() {
    yield takeEvery(CLEAR_TOASTS, handleClearToasts);
}

function* watchRunQueryOnFieldClicked() {
    yield takeLatest(checkActionForType(RUN_QUERY_ON_FIELD_CHECKED, reportTypes.mainReport), runQueryOnFieldClicked);
}

function* watchUpdateChart() {
    yield takeLatest(UPDATE_CHART, fetchChartData);
}

function* watchUpdateUserData() {
    yield takeEvery(UPDATE_USER_DATA, handleUserDataUpdated);
}

function* checkValidation(reportType) {
    const selectedReportsData = yield select(getQueryDataForType(reportType));
    const userData = yield select(getUserData);
    const { result } = validateQuery(selectedReportsData, userData.report_date_limit?.restrictions, reportType);

    if (result) {
        yield put(setActionType(setValidationError(''), reportType));
    }

    yield put(setActionType(setClearSelectionModeOff(), reportType));
}

function* watchFieldClicked() {
    for (let i = 0; i < reportConfigs.length; i++) {
        yield takeLatest(
            checkActionForType(FIELD_CLICKED, reportConfigs[i].type),
            checkValidation,
            reportConfigs[i].type
        );
    }
}

function* updateFieldsByReportType(reportType) {
    const fieldsDict = yield call(getFieldsDict);
    yield call(updateReportFields, fieldsDict, reportType);
}

function* watchSourcesChanged() {
    for (const reportType of REPORTS_WITH_DYNAMIC_FIELDS) {
        const actionType = checkActionForType(FILTER_VALUE_CHANGED, reportType);

        yield takeEvery(actionType, function*(action) {
            const { show } = yield select(getTableConfig(reportType));

            if (action.name === SOURCE_FIELD && !show) {
                yield call(updateFieldsByReportType, reportType);
            }
        });
    }
}

function* handleAIDimensionSelected(action) {
    const { field } = action;
    const { actual_type: fieldType, name } = field;

    if (!AI_DIMENSION_TYPES.includes(fieldType)) return;

    const reportType = reportTypes.creativeExplore;
    const reportConfig = getConfigForType(reportType);
    const selectedFields = yield select(reportConfig.selectors.selectedFields);

    const currentSelection = selectedFields[fieldType] || [];
    const isBeingAdded = currentSelection.includes(name);

    if (!isBeingAdded) return;

    const filters = yield select(reportConfig.selectors.filters);
    const existingFilterIndex = filters.findIndex(f => f.field === HAS_CREATIVE_PREVIEW_FILTER);

    // In case filter exists but has no values.
    if (existingFilterIndex !== -1) {
        const existingFilter = filters[existingFilterIndex];
        if (existingFilter.tags.length) return;
        yield put(setActionType(filterValueAdded(HAS_CREATIVE_PREVIEW_VALUE, existingFilterIndex), reportType));
    } else {
        yield put(setActionType(filterAdd(HAS_CREATIVE_PREVIEW_FILTER), reportType));
        const filtersAfterAdd = yield select(reportConfig.selectors.filters);
        const newFilterIndex = filtersAfterAdd.length - 1;
        yield put(setActionType(filterValueAdded(HAS_CREATIVE_PREVIEW_VALUE, newFilterIndex), reportType));
    }
}

function* watchAIDimensionSelected() {
    yield takeEvery(checkActionForType(FIELD_CLICKED, reportTypes.creativeExplore), handleAIDimensionSelected);
}

const watchers = [
    watchReportsModeSelected,
    watchDownloadReport,
    _runQueryWatcher(reportTypes.mainReport),
    _runQueryWatcher(reportTypes.pivot),
    _runQueryWatcher(reportTypes.anonymous),
    _runQueryWatcher(reportTypes.fraudProtected),
    _runQueryWatcher(reportTypes.fraudSuspicious),
    _runQueryWatcher(reportTypes.fraudRejected),
    _runQueryWatcher(reportTypes.adMonetization),
    _runQueryWatcher(reportTypes.crossDevice),
    _runQueryWatcher(reportTypes.skan),
    _runQueryWatcher(reportTypes.skanSummary),
    _runQueryWatcher(reportTypes.unifiedReport),
    _runQueryWatcher(reportTypes.creativeExplore),
    watchChartLinesChanged,
    watchChartTimeBreakdownChanged,
    watchTableFilterChange,
    watchDatesChangedGlobal,
    watchGenerateAnonymousLink,
    watchDeleteReport,
    watchShelfClose,
    watchGenerateAnonymousList,
    watchExportTable,
    watchAnonymousSecretIdUpdate,
    watchAnonymousPageRun,
    watchGetAlertsData,
    watchClearToasts,
    watchRunQueryOnFieldClicked,
    watchUpdateChart,
    watchUpdateUserData,
    watchFieldClicked,
    watchSourcesChanged,
    watchAIDimensionSelected,
];

export { onload, clearReport, destroy, watchers };
